import { useEffect, useState } from 'react'
import { Download, Eye, FileText, Loader2, AlertCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { attachmentsApi, type FormAttachment } from '@/api/attachmentsApi'

/** Human-readable size. */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export const isPreviewableImage = (mimeType: string): boolean =>
  mimeType === 'image/png' || mimeType === 'image/jpeg'

interface Props {
  submissionId: string
  fieldId: string
  attachment: Pick<FormAttachment, 'fileName' | 'mimeType' | 'sizeBytes' | 'storageKey'>
  /**
   * Open the full preview panel. Owned by the CALLER, not this component: this
   * renders inside a popover, and a dialog mounted here would be torn down the
   * moment the popover closed behind it.
   */
  onPreview: () => void
}

/**
 * Thumbnail + metadata for one stored attachment, used both by the field's own
 * control and by the read-only view of an EARLIER step's evidence.
 *
 * The download URL is fetched on mount rather than held in a query cache: it is
 * short-lived and single-use, so a cached one would hand back a dead link. That
 * also means the image bytes are only ever requested when a preview is actually
 * opened, not for every attachment on the page.
 */
export function AttachmentPreview({ submissionId, fieldId, attachment, onPreview }: Props) {
  const [url, setUrl] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  // `storageKey` — not just submissionId/fieldId — is the dependency that
  // actually matters: replacing a file keeps the same (submissionId, fieldId)
  // pair but points at a new object. Without this, a replace would keep
  // showing the PREVIOUS file's presigned URL while the caption above (driven
  // by fresh `attachment` props) already shows the new file's name/size.
  useEffect(() => {
    let cancelled = false
    setUrl(null)
    setError(null)
    attachmentsApi
      .getDownloadUrl(submissionId, fieldId)
      .then(res => {
        if (!cancelled) setUrl(res.url)
      })
      .catch((err: Error) => {
        if (!cancelled) setError(err.message)
      })
    return () => {
      cancelled = true
    }
  }, [submissionId, fieldId, attachment.storageKey])

  return (
    <div className='space-y-2'>
      <div className='flex items-start gap-2'>
        <FileText className='mt-0.5 h-4 w-4 shrink-0 text-muted-foreground' />
        <div className='min-w-0 flex-1'>
          <p className='truncate text-xs font-medium' title={attachment.fileName}>
            {attachment.fileName}
          </p>
          <p className='text-[11px] text-muted-foreground'>
            {formatBytes(attachment.sizeBytes)}
          </p>
        </div>
      </div>

      {error ? (
        <p className='flex items-center gap-1 text-[11px] text-destructive'>
          <AlertCircle className='h-3 w-3 shrink-0' />
          {error}
        </p>
      ) : !url ? (
        <div className='flex h-24 items-center justify-center rounded-md border bg-muted/30'>
          <Loader2 className='h-4 w-4 animate-spin text-muted-foreground' />
        </div>
      ) : (
        // The thumbnail is itself the way in — clicking what you are looking at
        // is the obvious gesture, and the buttons below are for readers who
        // want the choice spelled out.
        <button
          type='button'
          onClick={onPreview}
          title={`Preview ${attachment.fileName}`}
          className='block w-full overflow-hidden rounded-md border transition-colors hover:border-primary/50'
        >
          {isPreviewableImage(attachment.mimeType) ? (
            <img
              src={url}
              alt={attachment.fileName}
              className='max-h-48 w-full object-contain'
            />
          ) : (
            <span className='flex h-24 flex-col items-center justify-center gap-1 bg-muted/30 text-muted-foreground'>
              <FileText className='h-5 w-5 opacity-40' />
              <span className='text-[11px]'>Click to preview</span>
            </span>
          )}
        </button>
      )}

      {url && (
        <div className='flex gap-1.5'>
          <Button
            type='button'
            size='sm'
            className='h-7 flex-1 gap-1.5 text-xs'
            onClick={onPreview}
          >
            <Eye className='h-3.5 w-3.5' />
            Preview
          </Button>
          {/* Was labelled "Open / download", which was misleading: the URL is
              signed with Content-Disposition: attachment, so it only ever
              downloaded. Reading the file in place is Preview's job now. */}
          <Button
            type='button'
            size='sm'
            variant='outline'
            className='h-7 gap-1.5 text-xs'
            title='Download'
            onClick={() => window.open(url, '_blank', 'noopener,noreferrer')}
          >
            <Download className='h-3.5 w-3.5' />
          </Button>
        </div>
      )}
    </div>
  )
}
